import { ActionMenu } from 'common-ui/components/Table/ActionMenu'
import { ActionMenuRow } from 'common-ui/components/Table/ActionMenuContext'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import KeyIcon from '@mui/icons-material/Key'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { externalRoles, isRoleAuthorized } from 'common-billing-server/role'
import { Role, type Customer, type ExternalUser } from 'common-billing-server/types'

import { useBillingApi } from '../../api/billing/billing-context'
import { Path } from '../../routes/path'
import { formatExternalRole } from '../customer/externalRoleDisplay'
import Wrapper from '../common/Wrapper'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useAuth } from '../login/Auth'
import SecurityIcon from '@mui/icons-material/Security'

export const ExternalUserDetailsPage = () => {
    const { userId } = useParams()
    const navigate = useNavigate()
    const billingApi = useBillingApi()
    const { user: authenticatedUser } = useAuth()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [user, setUser] = useState<ExternalUser>()
    const [customers, setCustomers] = useState<Customer[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [resetDialogOpen, setResetDialogOpen] = useState(false)
    const [resetSent, setResetSent] = useState(false)
    const [accessDialogOpen, setAccessDialogOpen] = useState(false)
    const [customerId, setCustomerId] = useState('')
    const [role, setRole] = useState(Role.externalBasic)
    const [mfaResetDialogOpen, setMfaResetDialogOpen] = useState(false)

    useEffect(() => {
        const operationId = 'load-external-user'
        if (!userId) return
        removeFailedOperation(operationId)
        setLoading(true)
        void Promise.all([billingApi.getExternalUser(userId), billingApi.listCustomers({ limit: 0 })])
            .then(([externalUser, customerResult]) => {
                setUser(externalUser)
                setCustomers(customerResult.items)
            })
            .catch(() => addFailedOperation({ id: operationId, message: 'Failed fetching external user' }))
            .finally(() => setLoading(false))
    }, [addFailedOperation, billingApi, removeFailedOperation, userId])

    const availableCustomers = customers.filter(
        (customer) => !user?.customerAccess.some((access) => access.customer.id === customer.id)
    )

    const openAccessDialog = () => {
        setCustomerId(availableCustomers[0]?.id ?? '')
        setRole(Role.externalBasic)
        setAccessDialogOpen(true)
    }

    const setCustomerAccess = async (selectedCustomerId: string, selectedRole: Role) => {
        if (!user) return
        const operationId = `set-external-user-access-${selectedCustomerId}`
        removeFailedOperation(operationId)
        setSaving(true)
        try {
            setUser(await billingApi.setExternalUserCustomerAccess(user.id, selectedCustomerId, { role: selectedRole }))
            setAccessDialogOpen(false)
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed updating customer access', error })
        } finally {
            setSaving(false)
        }
    }

    const removeCustomerAccess = async (selectedCustomer: Pick<Customer, 'id' | 'name'>) => {
        if (!user || !window.confirm(`Remove access to ${selectedCustomer.name}?`)) return
        const operationId = `remove-external-user-access-${selectedCustomer.id}`
        removeFailedOperation(operationId)
        setSaving(true)
        try {
            setUser(await billingApi.deleteExternalUserCustomerAccess(user.id, selectedCustomer.id))
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed removing customer access', error })
        } finally {
            setSaving(false)
        }
    }

    const sendPasswordReset = async () => {
        if (!user) return
        const operationId = 'send-external-user-password-reset'
        removeFailedOperation(operationId)
        setSaving(true)
        try {
            await billingApi.requestExternalUserPasswordReset(user.id)
            setResetSent(true)
        } catch (error) {
            setResetDialogOpen(false)
            addFailedOperation({ id: operationId, message: 'Failed sending the password-reset email', error })
        } finally {
            setSaving(false)
        }
    }

    const openResetDialog = () => {
        setResetSent(false)
        setResetDialogOpen(true)
    }

    const deleteUser = async () => {
        if (!user || !window.confirm(`Delete ${user.username} and all customer access?`)) return
        setSaving(true)
        try {
            await billingApi.deleteExternalUser(user.id)
            void navigate(Path.externalUsers, { replace: true })
        } catch (error) {
            addFailedOperation({ id: 'delete-external-user', message: 'Failed deleting external user', error })
            setSaving(false)
        }
    }

    const resetMfa = async () => {
        if (!user) return
        setSaving(true)
        try {
            await billingApi.deleteExternalUserMfas(user.id)
            setUser({ ...user, mfa: { ...user.mfa, totpEnabled: false } })
            setMfaResetDialogOpen(false)
        } catch (error) {
            addFailedOperation({ id: 'reset-external-user-mfa', message: 'Failed resetting MFA', error })
        } finally {
            setSaving(false)
        }
    }

    const canResetMfa =
        user?.mfa.totpEnabled &&
        !!authenticatedUser &&
        isRoleAuthorized({ role: authenticatedUser.role, minimumAllowedRole: Role.admin })

    return (
        <Wrapper
            title={user?.username}
            actions={
                user ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            startIcon={<AddIcon />}
                            disabled={saving || availableCustomers.length === 0}
                            onClick={openAccessDialog}
                        >
                            Add customer access
                        </Button>
                        <Button startIcon={<KeyIcon />} disabled={saving} onClick={openResetDialog}>
                            Send password reset
                        </Button>
                        {canResetMfa && (
                            <Button
                                color="error"
                                startIcon={<SecurityIcon />}
                                disabled={saving}
                                onClick={() => setMfaResetDialogOpen(true)}
                            >
                                Reset MFA
                            </Button>
                        )}
                        <Button
                            color="error"
                            startIcon={<DeleteIcon />}
                            disabled={saving}
                            onClick={() => void deleteUser()}
                        >
                            Delete user
                        </Button>
                    </Box>
                ) : undefined
            }
        >
            {loading ? (
                <CircularProgress />
            ) : user ? (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Customer access
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Customer</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {user.customerAccess.map((access) => (
                                <ActionMenuRow key={access.customer.id}>
                                    <TableCell>{access.customer.name}</TableCell>
                                    <TableCell sx={{ width: 220 }}>
                                        <Select
                                            fullWidth
                                            size="small"
                                            inputProps={{ 'aria-label': `Role for ${access.customer.name}` }}
                                            value={access.role}
                                            disabled={saving}
                                            onChange={(event) =>
                                                void setCustomerAccess(access.customer.id, event.target.value as Role)
                                            }
                                        >
                                            {externalRoles.map((externalRole) => (
                                                <MenuItem key={externalRole} value={externalRole}>
                                                    {formatExternalRole(externalRole)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="right" padding="checkbox">
                                        <ActionMenu
                                            options={[
                                                {
                                                    label: 'Remove customer access',
                                                    icon: <DeleteIcon />,
                                                    disabled: saving || user.customerAccess.length === 1,
                                                    isDestructive: true,
                                                    onClick: () => void removeCustomerAccess(access.customer),
                                                },
                                            ]}
                                        >
                                            <Tooltip
                                                title={
                                                    user.customerAccess.length === 1
                                                        ? 'An external user must have at least one customer'
                                                        : 'Remove customer access'
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        aria-label={`Remove access to ${access.customer.name}`}
                                                        disabled={saving || user.customerAccess.length === 1}
                                                        onClick={() => void removeCustomerAccess(access.customer)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </ActionMenu>
                                    </TableCell>
                                </ActionMenuRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            ) : null}
            {errorBanner}

            <Dialog
                open={accessDialogOpen}
                onClose={saving ? undefined : () => setAccessDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <Box
                    component="form"
                    onSubmit={(event) => {
                        event.preventDefault()
                        void setCustomerAccess(customerId, role)
                    }}
                >
                    <DialogTitle>Add customer access</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                        <TextField
                            select
                            required
                            label="Customer"
                            value={customerId}
                            disabled={saving}
                            onChange={(event) => setCustomerId(event.target.value)}
                        >
                            {availableCustomers.map((customer) => (
                                <MenuItem key={customer.id} value={customer.id}>
                                    {customer.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            required
                            label="Role"
                            value={role}
                            disabled={saving}
                            onChange={(event) => setRole(event.target.value as Role)}
                        >
                            {externalRoles.map((externalRole) => (
                                <MenuItem key={externalRole} value={externalRole}>
                                    {formatExternalRole(externalRole)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAccessDialogOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" disabled={saving || !customerId}>
                            {saving ? 'Saving…' : 'Add access'}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Dialog
                open={resetDialogOpen}
                onClose={saving ? undefined : () => setResetDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <Box
                    component="form"
                    onSubmit={(event) => {
                        event.preventDefault()
                        void sendPasswordReset()
                    }}
                >
                    <DialogTitle>Send password reset to {user?.username}</DialogTitle>
                    <DialogContent>
                        {resetSent ? (
                            <Alert severity="success">
                                A single-use link for choosing a new password has been sent to {user?.username}.
                            </Alert>
                        ) : (
                            <Typography>
                                {user?.username} receives a single-use link for choosing a new password. The link
                                expires in one hour and replaces any link sent earlier.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setResetDialogOpen(false)} disabled={saving}>
                            {resetSent ? 'Close' : 'Cancel'}
                        </Button>
                        {resetSent ? null : (
                            <Button type="submit" variant="contained" disabled={saving}>
                                {saving ? 'Sending…' : 'Send reset link'}
                            </Button>
                        )}
                    </DialogActions>
                </Box>
            </Dialog>
            <Dialog open={mfaResetDialogOpen} onClose={() => !saving && setMfaResetDialogOpen(false)}>
                <DialogTitle>Reset MFA for {user?.username}?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Their authenticator credential and active sessions will be removed. Passkeys are preserved.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMfaResetDialogOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={() => void resetMfa()} disabled={saving}>
                        Reset MFA
                    </Button>
                </DialogActions>
            </Dialog>
        </Wrapper>
    )
}
